import dayjs, { type Dayjs } from 'src/helpers/dayjs';
import { isPublished } from 'src/helpers/publishedFilter';
import { sortDistanceIds } from 'src/helpers/statisticExtras';
import { resolveResourceId } from 'src/helpers/trackMapCompetitionIcons';
import { ICompetition } from 'src/interfaces/Competition';
import { IEdition, IEditionCompetition } from 'src/interfaces/Edition';
import {
  SignupTimelinePoint,
  SignupTimelineSeries,
} from 'src/interfaces/SignupTimeline';
import { TechnicalType } from 'src/interfaces/TechnicalType';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Format as UTC calendar day to match server `$dateToString` grouping. */
function utcDayKey(value: string | Date | Dayjs): string {
  return dayjs(value).utc().format('YYYY-MM-DD');
}

function isValidDay(date: string): boolean {
  return DATE_RE.test(date) && dayjs.utc(date).isValid();
}

/**
 * Zero-fills daily counts between first and last signup day (inclusive),
 * and builds a cumulative series.
 */
export function buildSignupTimelineSeries(
  points: readonly SignupTimelinePoint[],
): SignupTimelineSeries {
  const byDate = new Map<string, number>();
  for (const point of points) {
    if (!isValidDay(point.date)) continue;
    const count = Number(point.count);
    if (!Number.isFinite(count) || count < 0) continue;
    byDate.set(point.date, (byDate.get(point.date) ?? 0) + count);
  }

  if (byDate.size === 0) {
    return { labels: [], daily: [], cumulative: [] };
  }

  const sortedDays = [...byDate.keys()].sort();
  const first = sortedDays[0];
  const last = sortedDays[sortedDays.length - 1];

  const labels: string[] = [];
  const daily: number[] = [];
  const cumulative: number[] = [];
  let running = 0;

  let cursor = dayjs.utc(first);
  const end = dayjs.utc(last);
  while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
    const key = cursor.format('YYYY-MM-DD');
    const count = byDate.get(key) ?? 0;
    running += count;
    labels.push(key);
    daily.push(count);
    cumulative.push(running);
    cursor = cursor.add(1, 'day');
  }

  return { labels, daily, cumulative };
}

/** Aggregate registration createdAt values into daily points (client-side). */
export function pointsFromRegistrationDates(
  createdAts: readonly (string | Date | undefined | null)[],
): SignupTimelinePoint[] {
  const byDate = new Map<string, number>();
  for (const value of createdAts) {
    if (value == null) continue;
    const day = dayjs(value);
    if (!day.isValid()) continue;
    const key = utcDayKey(day);
    byDate.set(key, (byDate.get(key) ?? 0) + 1);
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({ date, count }));
}

export function getEditionCompetitionOptions(
  edition: Pick<IEdition, 'competitions'> | null | undefined,
  competitionsById?: Record<string, ICompetition>,
  options: { includeUnpublished?: boolean } = {},
): ICompetition[] {
  if (!edition?.competitions?.length || !competitionsById) return [];

  const { includeUnpublished = false } = options;

  return edition.competitions
    .map(ec => competitionsById[resolveResourceId(ec.competitionId)])
    .filter(
      (c): c is ICompetition =>
        c != null && (includeUnpublished || isPublished(c)),
    );
}

function editionCompetitionEntry(
  editionCompetitions: IEditionCompetition[],
  competitionId: string,
): IEditionCompetition | undefined {
  const target = resolveResourceId(competitionId);
  return editionCompetitions.find(
    entry => resolveResourceId(entry.competitionId) === target,
  );
}

export function getEditionDistanceIds(
  edition: Pick<IEdition, 'competitions'> | null | undefined,
  options: {
    /** @deprecated Prefer `competitionIds`. */
    competitionId?: string | null;
    competitionIds?: readonly string[] | null;
    distancesById?: Record<string, TechnicalType>;
    includeUnpublished?: boolean;
  } = {},
): string[] {
  if (!edition?.competitions?.length) return [];

  const { competitionId, competitionIds, distancesById, includeUnpublished = false } = options;
  const selectedCompetitionIds =
    competitionIds && competitionIds.length > 0
      ? competitionIds
      : competitionId
        ? [competitionId]
        : [];

  let ids: string[];

  if (selectedCompetitionIds.length > 0) {
    const seen = new Set<string>();
    ids = [];
    for (const selectedId of selectedCompetitionIds) {
      const entry = editionCompetitionEntry(edition.competitions, selectedId);
      for (const distanceId of entry?.distances ?? []) {
        const id = resolveResourceId(distanceId);
        if (!id || seen.has(id)) continue;
        seen.add(id);
        ids.push(id);
      }
    }
  } else {
    const seen = new Set<string>();
    ids = [];
    for (const entry of edition.competitions) {
      for (const distanceId of entry.distances ?? []) {
        const id = resolveResourceId(distanceId);
        if (!id || seen.has(id)) continue;
        seen.add(id);
        ids.push(id);
      }
    }
  }

  const visibleIds = includeUnpublished
    ? ids
    : ids.filter(id => {
        const distance = distancesById?.[id];
        return distance ? isPublished(distance) : true;
      });

  return sortDistanceIds(visibleIds, distancesById);
}

export function getEditionDistanceOptions(
  edition: Pick<IEdition, 'competitions'> | null | undefined,
  options: {
    /** @deprecated Prefer `competitionIds`. */
    competitionId?: string | null;
    competitionIds?: readonly string[] | null;
    distancesById?: Record<string, TechnicalType>;
    includeUnpublished?: boolean;
  } = {},
): TechnicalType[] {
  const { distancesById } = options;
  if (!distancesById) return [];
  return getEditionDistanceIds(edition, options)
    .map(id => distancesById[id])
    .filter((d): d is TechnicalType => Boolean(d));
}
